import { DEFAULT_LANGUAGE } from './languages';
import type { Dictionary, DictionarySet, NamespaceDictionaries } from './translate';

/**
 * Where the dashboard's dictionaries come from.
 *
 * Layout: `dictionaries/<lang>/<namespace>.ts`, each default-exporting a flat
 * `Record<string, string>` whose keys carry no namespace prefix (see the
 * conventions at the top of `dictionaries/en/common.ts`). Adding a namespace
 * or a language is adding a file - both globs below pick it up.
 *
 * English is bundled eagerly: it is the fallback for every other language and
 * what renders with no provider at all, so it must be there synchronously.
 * Every other language is a dynamic import, which Vite emits as its own
 * chunks, fetched only when someone switches to that language.
 */

type DictionaryModule = { default: Dictionary };

const englishModules = import.meta.glob<DictionaryModule>('./dictionaries/en/*.ts', { eager: true });

const lazyModules = import.meta.glob<DictionaryModule>([
  './dictionaries/*/*.ts',
  '!./dictionaries/en/*.ts',
]);

/** './dictionaries/hi/common.ts' -> ['hi', 'common'] */
function parsePath(path: string): [language: string, namespace: string] {
  const [, , language, file] = path.split('/');
  return [language, file.replace(/\.ts$/, '')];
}

const loaded: Record<string, Record<string, Dictionary>> = { [DEFAULT_LANGUAGE]: {} };

for (const [path, module] of Object.entries(englishModules)) {
  const [, namespace] = parsePath(path);
  loaded[DEFAULT_LANGUAGE][namespace] = module.default;
}

const pending = new Map<string, Promise<void>>();
const fetched = new Set<string>([DEFAULT_LANGUAGE]);

/** Languages that ship at least one dictionary file in this build. */
export function availableDictionaryLanguages(): string[] {
  const languages = new Set([DEFAULT_LANGUAGE]);
  for (const path of Object.keys(lazyModules)) languages.add(parsePath(path)[0]);
  return [...languages];
}

export function isLanguageLoaded(code: string): boolean {
  return fetched.has(code);
}

/**
 * Fetch every namespace of a language, once.
 *
 * Resolves (never rejects) - a language this build has no files for, or a
 * chunk that failed to download, simply leaves the English fallback in charge.
 * A failed load is forgotten so a later call can retry it.
 */
export function loadLanguage(code: string): Promise<void> {
  if (isLanguageLoaded(code)) return Promise.resolve();
  const inFlight = pending.get(code);
  if (inFlight) return inFlight;

  const entries = Object.entries(lazyModules).filter(([path]) => parsePath(path)[0] === code);
  const started = Promise.all(
    entries.map(async ([path, load]) => [parsePath(path)[1], (await load()).default] as const),
  )
    .then((namespaces) => {
      // Runtime registrations win over the bundled file, as they would if
      // they had been registered after it.
      const registered = loaded[code] ?? {};
      const merged: Record<string, Dictionary> = Object.fromEntries(namespaces);
      for (const [namespace, entries] of Object.entries(registered)) {
        merged[namespace] = { ...(merged[namespace] ?? {}), ...entries };
      }
      loaded[code] = merged;
      fetched.add(code);
    })
    .catch((error: unknown) => {
      if (import.meta.env.DEV) console.warn(`[i18n] Could not load "${code}" dictionaries`, error);
    })
    .finally(() => {
      pending.delete(code);
    });

  pending.set(code, started);
  return started;
}

/**
 * Add or extend a namespace at runtime - for tests, and for a feature that
 * wants to ship copy outside the dictionaries folder.
 */
export function registerDictionary(code: string, namespace: string, entries: Dictionary): void {
  loaded[code] = {
    ...(loaded[code] ?? {}),
    [namespace]: { ...(loaded[code]?.[namespace] ?? {}), ...entries },
  };
}

/** A snapshot of everything loaded so far. */
export function loadedDictionaries(): DictionarySet {
  return loaded as Record<string, NamespaceDictionaries>;
}
